
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const COURSE_URL = "https://fetc.in/api/v1/courses";

export const courseApi = createApi({
reducerPath: "coursesApi",
tagTypes: ["Courses", "Progress"],

baseQuery: fetchBaseQuery({
baseUrl: COURSE_URL,
credentials: "include", // send cookies/JWT
}),

endpoints: (builder) => ({


/* -----------------------------------------
   CREATE COURSE (FormData)
----------------------------------------- */
createCourse: builder.mutation({
  query: (formData) => ({
    url: "/create",
    method: "POST",
    body: formData,
  }),
  invalidatesTags: ["Courses"],
}),

/* -----------------------------------------
   GET COURSES
----------------------------------------- */
getCourses: builder.query({
  query: () => "/list",
  providesTags: ["Courses"],
}),

getPublishedCourses: builder.query({
  query: () => "/published",
  providesTags: ["Courses"],
}),

getCreatorCourses: builder.query({
  query: () => "/user",
  providesTags: ["Courses"],
}),

/* -----------------------------------------
   GET COURSE BY SLUG
----------------------------------------- */
getCourseBySlug: builder.query({
  query: (slug) => `/${slug}`,
  providesTags: (result, error, slug) => [
    { type: "Courses", id: slug }
  ],
  keepUnusedDataFor: 0,
}),

/* -----------------------------------------
   ENROLL COURSE
----------------------------------------- */
enrollCourse: builder.mutation({
  query: (courseId) => ({
    url: `/${courseId}/enroll`,
    method: "POST",
  }),
  invalidatesTags: (r, e, courseId) => [
    { type: "Courses", id: courseId },
    "Progress",
  ],
}),

/* -----------------------------------------
   PROGRESS DATA
----------------------------------------- */
getCourseProgress: builder.query({
  query: (courseId) => `/${courseId}/progress`,
  providesTags: (r, e, courseId) => [
    { type: "Progress", id: courseId }
  ],
  transformResponse: (response) => ({
  lessonsProgress: Array.isArray(response.lessonsProgress)
    ? response.lessonsProgress
    : [],
  percentage: Number(response.percentage) || 0,
  score: Number(response.score) || 0,
  isCompleted: !!response.isCompleted,
}),

}),

/* -----------------------------------------
   COURSE RESULT
----------------------------------------- */
getCourseResult: builder.query({
  query: (courseId) => `/${courseId}/result`,
}),

saveCourseResult: builder.mutation({
  query: ({ courseId, result }) => ({
    url: `/${courseId}/result`,
    method: "POST",
    body: result,
  }),
  invalidatesTags: (r, e, { courseId }) => [
    { type: "Progress", id: courseId },
  ],
}),

/* -----------------------------------------
   EDIT COURSE
----------------------------------------- */
editCourse: builder.mutation({
  query: ({ slug, data }) => ({
    url: `/${slug}`,
    method: "PUT",
    body: data,
  }),
  invalidatesTags: (r, e, { slug }) => [
    { type: "Courses", id: slug },
    "Courses",
  ],
}),

/* -----------------------------------------
   DELETE COURSE
----------------------------------------- */
deleteCourse: builder.mutation({
  query: (slug) => ({
    url: `/${slug}`,
    method: "DELETE",
  }),
  invalidatesTags: ["Courses"],
}),

/* -----------------------------------------
   FILE UPLOAD HELPERS
----------------------------------------- */
uploadCourseFile: builder.mutation({
  query: (formData) => ({
    url: "/upload/file",
    method: "POST",
    body: formData,
  }),
}),

uploadCourseAudio: builder.mutation({
  query: (formData) => ({
    url: "/upload/audio",
    method: "POST",
    body: formData,
  }),
}),


uploadCourseImage: builder.mutation({
  query: (formData) => ({
    url: "/upload/image",
    method: "POST",
    body: formData,
  }),
}),

uploadCourseVideo: builder.mutation({
  query: (formData) => ({
    url: "/upload/video",
    method: "POST",
    body: formData,
  }),
}),

/* -----------------------------------------
   ADD LESSON
----------------------------------------- */
addLesson: builder.mutation({
  query: ({ courseSlug, lessonData }) => ({
    url: `/${courseSlug}/lessons`,
    method: "POST",
    body: lessonData, // can be JSON or FormData
  }),
  invalidatesTags: (r, e, { courseSlug }) => [
    { type: "Courses", id: courseSlug },
  ],
}),



/* -----------------------------------------
   EDIT LESSON
----------------------------------------- */
editLesson: builder.mutation({
  query: ({ courseSlug, lessonId, lessonData }) => ({
    url: `/${courseSlug}/lessons/${lessonId}`,
    method: "PUT",
    body: lessonData,  // can be JSON or FormData
  }),
  invalidatesTags: (r, e, { courseSlug }) => [
    { type: "Courses", id: courseSlug },
  ],
}),


deleteLesson: builder.mutation({
  query: ({ courseSlug, lessonId }) => ({
    url: `/${courseSlug}/lessons/${lessonId}`,
    method: "DELETE",
  }),
  invalidatesTags: (r, e, { courseSlug }) => [
    { type: "Courses", id: courseSlug },
  ],
}),

/* -----------------------------------------
   UPDATE LESSON COMPLETION
----------------------------------------- */
updateLessonCompletion: builder.mutation({
  query: ({ courseId, lessonId, completed, score }) => ({
    url: `/${courseId}/lessons/${lessonId}/completion`,
    method: "PUT",
    body: { completed, score },
  }),
  invalidatesTags: (r, e, { courseId }) => [
    { type: "Progress", id: courseId },
  ],
}),

startLessonExercise: builder.mutation({
  query: ({ courseId, lessonId }) => ({
    url: `/student/lessons/${lessonId}/start`,
    method: "POST",
  }),
}),

// Submit attempt
submitLessonExercise: builder.mutation({
  query: ({ attemptId, answers }) => ({
    url: `/student/attempts/${attemptId}/submit`,
    method: "POST",
    body: { answers },
  }),
}),

// Get result
getLessonResult: builder.query({
  query: (attemptId) => `/student/attempts/${attemptId}/result`,
}),

}),
});

/* -----------------------------------------
EXPORTED HOOKS
----------------------------------------- */
export const {
useCreateCourseMutation,
useGetCoursesQuery,
useGetPublishedCoursesQuery,
useGetCreatorCoursesQuery,
useGetCourseBySlugQuery,
useEnrollCourseMutation,
useGetCourseProgressQuery,
useEditCourseMutation,
useDeleteCourseMutation,
useAddLessonMutation,
useEditLessonMutation,
useGetCourseResultQuery,
useUpdateLessonCompletionMutation,
useSaveCourseResultMutation,
useUploadCourseFileMutation,
useUploadCourseImageMutation,
useUploadCourseVideoMutation,
useUploadCourseAudioMutation,
useDeleteLessonMutation,
} = courseApi;
